import { RootState } from '@/store/types';
import {
  Evaluation,
  EvaluationsState,
  EvaluationConditions,
  GradeEvaluationConditions,
} from '@/store/evaluations/types';
import axios from 'axios';
import _ from 'lodash'
import { Commit, ActionTree } from 'vuex';
import { getPagerFromApiResponse } from '@/store/utils';
import moment from 'moment'

const actions: ActionTree<EvaluationsState, RootState> = {
  fetchEvaluations: async (
    { commit }: {commit: Commit},
    { page, conditions } : { conditions?: EvaluationConditions, page: number },
  ) => {
    const { headers, data } = await axios.get('/evaluations', {
      params: {
        ..._.pickBy({
          tutor: conditions?.tutorNameOrId,
          positionCode: conditions?.positions?.map(({ code }) => code).join(','),
          gender: conditions?.genders?.join(','),
          nationalityCode: conditions?.nationality?.code,
          fiscalYear: conditions?.year,
        }),
        page,
      },
    })

    commit('fetchedEvaluations', {
      pager: getPagerFromApiResponse(page, headers),
      evaluations: data,
    });
  },

  /**
   * APIのレスポンスからチューター最終評価の詳細を作成
   * @param {string} tutorId ログインしているチューターのID
   * @param {number} fiscalYear 年度
   */
  getEvaluation: async (
    { commit }: {commit: Commit},
    { tutorId, fiscalYear } : { tutorId: string, fiscalYear: number },
  ) => {
    const { data } = await axios.get(`/tutors/${tutorId}/evaluations`, {
      params: {
        fiscalYear,
      },
    });

    const evaluation: Evaluation = {
      tutorId: data.tutorId,
      tutorName: data.tutorName,
      position: data.position,
      hireDate: moment(data.hireDate, 'YYYYMMDD'),
      fiscalYear: data.fiscalYear,
      totalScore: data.totalScore,
      observationTotalScore: data.observationTotalScore,
      observationScore: data.observationScore,
      observationFormScore: data.observationFormScore,
      administrativeDutiesTotalScore: data.administrativeDutiesTotalScore,
      commentScore: data.commentScore,
      reviewSheetScore: data.reviewSheetScore,
      deadlineScore: data.deadlineScore,
      lessonReviewTaskScore: data.lessonReviewTaskScore,
      studentInquiryScore: data.studentInquiryScore,
      performanceScore: data.performanceScore,
      attendanceScore: data.attendanceScore,
      medianTotalScore: data.medianTotalScore,
    }

    commit('gotEvaluation', evaluation);
  },

  importEvaluationsByCsv: (context, csvFile: File) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    return axios.post('/evaluations/import', formData);
  },

  exportEvaluationsByCsv: async (context, { conditions }: { conditions: EvaluationConditions }) => {
    const { data, headers } = await axios.get('/evaluations/csv', {
      params: {
        ..._.pickBy({
          tutor: conditions?.tutorNameOrId,
          positionCode: conditions?.positions?.map(({ code }) => code).join(','),
          gender: conditions?.genders?.join(','),
          nationalityCode: conditions?.nationality?.code,
          fiscalYear: conditions?.year,
        }),
      },
    });

    const filename = _.chain(headers)
      .get('content-disposition')
      .split(';')
      .reduce((carry, directive) => {
        const [empty, name] = directive.trim().split('filename=');
        return name ?? carry;
      }, {})
      .value();

    return {
      content: data,
      filename,
    }
  },

  /**
   * 成績評価のcsvを作成
   * @param {GradeEvaluationConditions} conditions 検索条件
   */
  exportGradeEvaluationByCsv: async (context, conditions: GradeEvaluationConditions) => {
    const { data } = await axios.get('/students/gradeEvaluations/csv', {
      params: {
        ..._.pickBy({
          fiscalYear: conditions?.year,
          universityCode: conditions?.university,
          semesterCode: conditions?.semester,
          subjectCode: conditions?.subject,
        }),
      },
    });

    return {
      content: data,
    }
  },
};

export default actions;
